import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Button, Form, Row, Col, Card, Spinner as BootstrapSpinner } from 'react-bootstrap';
import { FaBuilding, FaUser, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';
import TasksService from '../services/TasksService';
import OrganizationalUnitService from '../services/OrganizationalUnitService';
import ProfileImage from './ProfileImage';
import Spinner from './Spinner';
import Select from 'react-select';
import '../assets/styles/TaskDetail.css';

// Helper to format date
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    } catch {
        return dateString;
    }
};

// Helper to get compliance status color
const getComplianceStatusColor = (status) => {
    switch (status) {
        case 'OnTrack':
            return '#198754';
        case 'AtRisk':
            return '#ffc107';
        case 'Breached':
            return '#dc3545';
        case 'Completed':
            return '#0dcaf0';
        default:
            return '#6c757d';
    }
};

// Helper to calculate time remaining
const calculateTimeRemaining = (dueDate) => {
    if (!dueDate) return null;
    const now = new Date();
    const due = new Date(dueDate);
    const diff = due - now;
    
    if (diff < 0) return 'Overdue';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 48) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
};

const TaskDetailPage = () => {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [comment, setComment] = useState('');
    const [canEdit, setCanEdit] = useState(false);
    const [predictedApprovers, setPredictedApprovers] = useState(null);
    
    // Dynamic Assignment States
    const [isDynamicAssignment, setIsDynamicAssignment] = useState(false);
    const [orgUnits, setOrgUnits] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState('');
    const [approvalType, setApprovalType] = useState('AllUsers');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [unitUsers, setUnitUsers] = useState([]);
    const [loadingUnits, setLoadingUnits] = useState(false);
    const [performingAction, setPerformingAction] = useState(false);
    const [showApproveDropdown, setShowApproveDropdown] = useState(false);

    const notifyTaskListRefresh = () => {
        try {
            localStorage.setItem('tasks:lastRefresh', String(Date.now()));
            window.dispatchEvent(new CustomEvent('tasks-updated'));
        } catch {
            // best-effort UI sync; ignore storage/event failures
        }
    };

    useEffect(() => {
        loadTaskDetails();
    }, [taskId, user]);

    // Load organizational units when task has dynamic assignment step
    useEffect(() => {
        if (task?.currentStepIsDynamic) {
            setIsDynamicAssignment(true);
            loadOrganizationalUnits();
        } else {
            setIsDynamicAssignment(false);
            setSelectedUnit('');
            setSelectedUsers([]);
        }
    }, [task?.currentStepIsDynamic]);

    const loadTaskDetails = async () => {
        try {
            setLoading(true);
            setError(null);
            const [taskDetails, prediction] = await Promise.all([
                TasksService.getTaskDetails(taskId, user.userId),
                TasksService.getPredictedNextApprovers(taskId, user.userId).catch(() => null)
            ]);
            setTask(taskDetails);
            setPredictedApprovers(prediction);
            
            // Check if user can edit their action
            if (taskDetails.currentUserAction && taskDetails.currentUserAction !== 'Pending') {
                const canEditAction = await TasksService.canEditTaskAction(taskId, user.userId);
                setCanEdit(canEditAction);
                
                // Debug logging
                console.log('=== TASK DETAIL EDIT DEBUG ===');
                console.log('Task ID:', taskId);
                console.log('User ID:', user.userId);
                console.log('Current User Action:', taskDetails.currentUserAction);
                console.log('Task Status:', taskDetails.status);
                console.log('Can Edit:', canEditAction);
                console.log('Activities:', taskDetails.activities);
                console.log('==============================');
            }
        } catch (error) {
            console.error('Error loading task details:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const loadOrganizationalUnits = async () => {
        try {
            setLoadingUnits(true);
            const units = await OrganizationalUnitService.getOrganizationChart();
            setOrgUnits(units || []);
        } catch (error) {
            console.error('Error loading organizational units:', error);
        } finally {
            setLoadingUnits(false);
        }
    };

    // Helper function to render organizational units hierarchically
    const renderUnitOption = (unit) => {
        const hasUsers = unit.assignedUsers && unit.assignedUsers.length > 0;
        return (
            <React.Fragment key={unit.id}>
                <option value={unit.id} disabled={!hasUsers}>
                    {unit.name}
                    {unit.assignedUsers && unit.assignedUsers.length > 0 && ` (${unit.assignedUsers.length} users)`}
                </option>
                {unit.children && unit.children.length > 0 && unit.children.map(child => renderUnitOption(child))}
            </React.Fragment>
        );
    };

    const handleUnitChange = async (unitId) => {
        setSelectedUnit(unitId);
        setSelectedUsers([]);
        setUnitUsers([]);
        if (unitId) {
            try {
                const users = await OrganizationalUnitService.getUsersByUnitId(unitId);
                const formattedUsers = users.map(u => ({
                    value: u.id,
                    label: `${u.firstName} ${u.lastName}`
                }));
                setUnitUsers(formattedUsers || []);
            } catch (error) {
                console.error('Error loading unit users:', error);
            }
        }
    };

    const performDynamicAssignmentAction = async (action) => {
        const isTerminateAction = action === 'Terminate';

        if (!isTerminateAction && !selectedUnit) {
            setError('Please select a unit');
            return;
        }

        if (!isTerminateAction && approvalType === 'SpecificUsers' && selectedUsers.length === 0) {
            setError('Please select at least one user');
            return;
        }

        try {
            setPerformingAction(true);
            setError(null);
            
            // Convert selected users from react-select format to ID array
            const selectedUserIds = !isTerminateAction && approvalType === 'SpecificUsers' 
                ? selectedUsers.map(user => Number(user.value))
                : [];
            
            await TasksService.assignTaskToUnit(
                taskId,
                isTerminateAction ? 0 : parseInt(selectedUnit),
                isTerminateAction ? 'AllUsers' : approvalType,
                selectedUserIds,
                comment,
                user.userId,
                action
            );
            notifyTaskListRefresh();
            navigate('/user-dashboard');
        } catch (error) {
            console.error('Error performing dynamic assignment action:', error);
            setError(error.message || 'Error performing action. Please try again.');
        } finally {
            setPerformingAction(false);
        }
    };

    const handleApprove = async () => {
        try {
            if (isDynamicAssignment) {
                if (!showApproveDropdown) {
                    // First click - show dropdown
                    setShowApproveDropdown(true);
                    loadOrganizationalUnits();
                } else {
                    // Second click (after selection) - perform action
                    await performDynamicAssignmentAction('Approve');
                }
            } else {
                await TasksService.performTaskAction(taskId, 'Approve', comment, user.userId);
                notifyTaskListRefresh();
                navigate('/user-dashboard');
            }
        } catch (error) {
            setError(error.message);
        }
    };

    const handleReject = async () => {
        try {
            if (isDynamicAssignment) {
                const confirmed = window.confirm('Are you sure you want to terminate this task? This will end the workflow and no reassignment will happen.');
                if (!confirmed) return;

                await performDynamicAssignmentAction('Terminate');
            } else {
                await TasksService.performTaskAction(taskId, 'Reject', comment, user.userId);
                notifyTaskListRefresh();
                navigate('/user-dashboard');
            }
        } catch (error) {
            setError(error.message);
        }
    };

    const handleEdit = async () => {
        // Confirm before rollback
        const confirmed = window.confirm('Are you sure you want to rollback your last action? This will undo your previous decision and allow the workflow to continue from the previous state.');
        
        if (!confirmed) return;
        
        try {
            // Use the proper rollback functionality
            await TasksService.editTaskAction(taskId, user.userId);
            
            // Show success message and redirect or reload
            console.log('Action rolled back successfully');
            alert('Your last action has been rolled back successfully.');
            notifyTaskListRefresh();
            navigate('/user-dashboard'); // or you could reload the task details
        } catch (error) {
            console.error('Error rolling back action:', error);
            setError(error.message);
        }
    };



    const renderEntityDetails = () => {
        if (!task?.entityDetails) return null;

        return Object.entries(task.entityDetails).map(([key, value]) => {
            // Skip internal fields
            if (key === 'id' || key === 'requestId') return null;

            // Format the display name
            const displayName = key
                .replace(/([A-Z])/g, ' $1') // Add space before capital letters
                .replace(/^./, str => str.toUpperCase()); // Capitalize first letter

            // Format the value based on field type
            let displayValue;
            if (key.toLowerCase().includes('date') || key === 'createdAt' || key === 'updatedAt') {
                displayValue = value ? formatDate(value) : '-';
            } else if (typeof value === 'boolean') {
                displayValue = value ? 'Yes' : 'No';
            } else if (key === 'totalDays') {
                displayValue = `${value} day${Math.abs(value) !== 1 ? 's' : ''}`;
            } else {
                displayValue = value || '-';
            }

            return (
                <div key={key} className="task-detail-entity-field">
                    <strong className="task-detail-entity-label">{displayName}:</strong>{' '}
                    <span className="task-detail-entity-value">{displayValue}</span>
                </div>
            );
        });
    };

    if (loading) {
        return <Spinner />;
    }

    if (error) {
        return (
            <Container>
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </Container>
        );
    }

    if (!task) return null;

    const initiatorImg = task.initiatedByUserProfileImage;
    const initiatorName = task.initiatedByUserName;
    const initiatorEmail = task.initiatedByUserEmail;
    const initiatorPersonelCode = task.initiatedByUserPersonelCode;
    const requestNumber = task.entityDetails?.requestId || task.entityDetails?.id || task.id;
    const activities = task.activities || [];

    const normalizeActionLabel = (action) => {
        if (!action) return 'Processed';
        const normalized = action.toLowerCase();
        if (normalized === 'approve') return 'Approved';
        if (normalized === 'reject') return 'Rejected';
        if (normalized === 'terminate') return 'Terminated';
        if (normalized === 'cancel') return 'Cancelled';
        if (normalized === 'dynamicassignment' || normalized === 'reassign') return 'Reassigned';
        if (normalized === 'initiate' || normalized === 'initiated' || normalized === 'start' || normalized === 'started' || normalized === 'submit' || normalized === 'submitted') return 'Initiated';
        return action;
    };

    const getActionIcon = (action) => {
        if (!action) return '⚡';
        const normalized = action.toLowerCase();
        if (normalized === 'approve') return '✅';
        if (normalized === 'reject') return '❌';
        if (normalized === 'terminate' || normalized === 'cancel') return '🛑';
        if (normalized === 'dynamicassignment' || normalized === 'reassign') return '🔁';
        return '⚡';
    };

    const buildPendingApproverText = () => {
        const candidates = predictedApprovers?.candidates || [];
        if (!candidates.length) return null;

        const names = candidates
            .map(c => c.userName)
            .filter(Boolean);

        if (!names.length) return null;

        if (names.length === 1) {
            return names[0];
        }

        const preview = names.slice(0, 3).join(', ');
        const remaining = names.length - 3;
        if (remaining > 0) {
            return `${preview} +${remaining} more`;
        }

        return preview;
    };

    // Build timeline data
    const timelineSteps = [
        {
            title: 'Request Created',
            performer: `Created by ${task.initiatedByUserName}`,
            date: task.startedAt,
            status: 'completed',
            icon: '📄'
        },
        ...activities.map((a) => ({
            title: a.workflowStepName || a.action || 'Step',
            performer: a.performedByUserName ? 
                `${normalizeActionLabel(a.action)} by ${a.performedByUserName}` : 
                null,
            date: a.performedAt,
            status: 'completed',
            icon: getActionIcon(a.action),
            comments: a.comments
        }))
    ];

    // Add current step only if workflow is not completed/rejected/cancelled
    if (task.currentStepName && task.status === 'InProgress') {
        timelineSteps.push({
            title: task.currentStepName,
            performer: buildPendingApproverText(),
            date: null,
            status: 'pending',
            icon: '⏳'
        });
    }

    return (
        <Container>
            <div className="task-detail-container">
                {/* Profile Image and User Details */}
                <div className="task-detail-profile">
                    <ProfileImage imageUrl={initiatorImg} width={120} height={120} className="task-detail-profile-image" />
                    <h5>{initiatorName}</h5>
                    <div className="profile-email">{initiatorEmail}</div>
                    <div className="profile-personnel">Personnel Code: {initiatorPersonelCode}</div>
                </div>
                
                {/* Request Number */}
                <div className="request-number-section">
                    <div className="request-number-label">REQUEST NUMBER</div>
                    <div className="request-number-value">#{requestNumber}</div>
                </div>
                
                {/* Suspension Status */}
                {task.isSuspended && (
                    <div style={{
                        marginBottom: '12px',
                        padding: '8px',
                        borderRadius: '4px',
                        backgroundColor: '#cfe2ff',
                        borderLeft: '4px solid #0d6efd'
                    }}>
                        <strong style={{ color: '#084298' }}>🔒 Suspended</strong>
                        {task.suspensionReason && (
                            <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: '#084298' }}>
                                {task.suspensionReason}
                            </p>
                        )}
                    </div>
                )}

                {/* Recovery Status */}
                {task.wasBreachedButRecovered && (
                    <div style={{
                        padding: '8px',
                        borderRadius: '4px',
                        backgroundColor: '#d1e7dd',
                        borderLeft: '4px solid #198754'
                    }}>
                        <strong style={{ color: '#0f5132' }}>✓ Recovered from breach</strong>
                    </div>
                )}

                {/* Escalation Chain */}
                {task.escalationChain && (
                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #dee2e6' }}>
                        <strong>Escalation Chain:</strong>
                        <p style={{ margin: '4px 0 0 0', fontSize: '0.875rem', color: '#6c757d', fontFamily: 'monospace' }}>
                            {task.escalationChain}
                        </p>
                    </div>
                )}
                
                {/* Request Details */}
                <div className="task-detail-section">
                    <h6 className="task-detail-section-header">Request Details</h6>
                    <div className="task-detail-card">
                        {renderEntityDetails()}
                    </div>
                </div>
                
                {/* Activity Timeline */}
                <div className="task-detail-section">
                    <h6 className="task-detail-section-header">Activity Timeline</h6>
                    <div className="task-detail-timeline">
                        <div 
                            className="task-detail-timeline-container"
                            style={{
                                '--timeline-progress': `${Math.min(100, (timelineSteps.filter(s => s.status === 'completed').length / timelineSteps.length) * 100)}%`,
                                '--step-width': `${100 / timelineSteps.length}%`
                            }}
                        >
                            {/* Timeline line */}
                            <div className="task-detail-timeline-line" />
                            
                            {/* Timeline steps */}
                            {timelineSteps.map((step, index) => (
                                <div 
                                    key={index} 
                                    className="task-detail-timeline-step"
                                >
                                    {/* Step circle */}
                                    <div 
                                        className={`task-detail-timeline-circle ${
                                            step.status === 'completed' ? 'status-completed' : 
                                            step.status === 'pending' ? 'status-pending' : 'status-default'
                                        }`}
                                    >
                                        {step.status === 'completed' ? '✓' : 
                                         step.status === 'pending' ? '⏳' : '○'}
                                    </div>
                                    
                                    {/* Step content */}
                                    <div className="task-detail-timeline-content">
                                        <div className="task-detail-timeline-title">
                                            {step.title}
                                        </div>
                                        
                                        {step.performer && (
                                            <div className="task-detail-timeline-performer">
                                                {step.performer}
                                            </div>
                                        )}
                                        
                                        {step.date && (
                                            <div className="task-detail-timeline-date">
                                                {formatDate(step.date)}
                                            </div>
                                        )}
                                        
                                        {step.comments && (
                                            <div className="task-detail-timeline-comments">
                                                "{step.comments}"
                                            </div>
                                        )}
                                        
                                        {step.status === 'pending' && (
                                            <div className="task-detail-timeline-pending">
                                                PENDING
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                
                {/* Action Section */}
                <div className="task-detail-section">
                    {task.currentUserAction === 'Pending' ? (
                        <>
                            {isDynamicAssignment && task.currentStepIsDynamic ? (
                                // Dynamic Assignment - Must Reassign to next approvers
                                <div className={`task-detail-actions ${showApproveDropdown ? 'task-detail-actions-dropdown' : ''}`}>
                                    {!showApproveDropdown ? (
                                        // Initial buttons - Reassign (mandatory) or Terminate
                                        <>
                                            <Button 
                                                variant="danger" 
                                                className="task-detail-action-button" 
                                                onClick={handleReject}
                                            >
                                                Terminate
                                            </Button>
                                            <Form.Control
                                                type="text"
                                                placeholder="My Comment about the Request"
                                                value={comment}
                                                onChange={e => setComment(e.target.value)}
                                                className="task-detail-comment-input"
                                            />
                                            <Button 
                                                variant="success" 
                                                className="task-detail-action-button" 
                                                onClick={handleApprove}
                                            >
                                                Reassign <small>(Select Next Approver)</small>
                                            </Button>
                                        </>
                                    ) : (
                                        // Reassignment form - user MUST select next approvers
                                        <>
                                            {loadingUnits ? (
                                                <div className="text-center my-3">
                                                    <BootstrapSpinner animation="border" size="sm" />
                                                </div>
                                            ) : (
                                                <>
                                                    {/* Unit Selection */}
                                                    <Form.Group className="mb-3">
                                                        <Form.Label className="fw-bold">Select Organizational Unit {selectedUnit && unitUsers.length > 0 && `(${unitUsers.length} users)`}</Form.Label>
                                                        <Form.Select
                                                            value={selectedUnit || ''}
                                                            onChange={(e) => handleUnitChange(e.target.value)}
                                                        >
                                                            <option value="">-- Choose a Unit --</option>
                                                            {orgUnits.map(unit => renderUnitOption(unit))}
                                                        </Form.Select>
                                                    </Form.Group>

                                                    {/* Cancel Button - Only visible before selecting unit */}
                                                    {!selectedUnit && (
                                                        <Button 
                                                            variant="secondary" 
                                                            className="w-100 mb-3"
                                                            onClick={() => {
                                                                setShowApproveDropdown(false);
                                                                setSelectedUnit('');
                                                                setSelectedUsers([]);
                                                                setApprovalType('AllUsers');
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    )}

                                                    {/* Approval Type Selection */}
                                                    {selectedUnit && (
                                                        <Form.Group className="mb-2">
                                                            <Form.Label className="mb-1"><strong>Approval Type</strong></Form.Label>
                                                            <div className="d-flex flex-column gap-1">
                                                                <Form.Check
                                                                    className="mb-1"
                                                                    type="radio"
                                                                    id="allUsers"
                                                                    label="All Users in Unit"
                                                                    name="approvalType"
                                                                    value="AllUsers"
                                                                    checked={approvalType === 'AllUsers'}
                                                                    onChange={(e) => {
                                                                        setApprovalType(e.target.value);
                                                                        setSelectedUsers([]);
                                                                    }}
                                                                />
                                                                <Form.Check
                                                                    className="mb-0"
                                                                    type="radio"
                                                                    id="specificUsers"
                                                                    label="Specific Users"
                                                                    name="approvalType"
                                                                    value="SpecificUsers"
                                                                    checked={approvalType === 'SpecificUsers'}
                                                                    onChange={(e) => {
                                                                        setApprovalType(e.target.value);
                                                                        setSelectedUsers([]);
                                                                        setUnitUsers([]); // Clear to force reload
                                                                    }}
                                                                />
                                                            </div>
                                                        </Form.Group>
                                                    )}

                                                    {/* Specific Users Selection */}
                                                    {approvalType === 'SpecificUsers' && (
                                                        <Form.Group className="mb-3">
                                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                                <Form.Label className="mb-0"><strong>Select Users {selectedUsers.length > 0 && `(${selectedUsers.length} selected)`}</strong></Form.Label>
                                                                <Button 
                                                                    variant="link" 
                                                                    size="sm" 
                                                                    onClick={() => setSelectedUsers(unitUsers)}
                                                                    className="p-0"
                                                                >
                                                                    Select All
                                                                </Button>
                                                            </div>
                                                            {unitUsers.length > 0 ? (
                                                                <Select
                                                                    isMulti
                                                                    options={unitUsers}
                                                                    value={selectedUsers}
                                                                    onChange={(selected) => setSelectedUsers(selected)}
                                                                    placeholder="Select users who will approve the task..."
                                                                />
                                                            ) : (
                                                                <p className="text-muted">No users in selected unit</p>
                                                            )}
                                                        </Form.Group>
                                                    )}

                                                    {selectedUnit && (
                                                        <>
                                                            {/* Comment Input */}
                                                            <Form.Group className="mb-2">
                                                                <Form.Label className="mb-1"><strong>Comments</strong></Form.Label>
                                                                <Form.Control
                                                                    as="textarea"
                                                                    rows={3}
                                                                    placeholder="Add your comments (optional)"
                                                                    value={comment}
                                                                    onChange={e => setComment(e.target.value)}
                                                                />
                                                            </Form.Group>

                                                            {/* Action Buttons */}
                                                            <div className="d-flex flex-column gap-2">
                                                                <Button 
                                                                    variant="success" 
                                                                    className="w-100"
                                                                    onClick={() => handleApprove()}
                                                                    disabled={performingAction || !selectedUnit || (approvalType === 'SpecificUsers' && selectedUsers.length === 0)}
                                                                    title={!selectedUnit ? 'Please select a unit' : approvalType === 'SpecificUsers' && selectedUsers.length === 0 ? 'Please select at least one user' : ''}
                                                                >
                                                                    {performingAction ? <BootstrapSpinner animation="border" size="sm" className="me-2" /> : <FaCheckCircle className="me-2" />}
                                                                    Reassign & Approve
                                                                </Button>
                                                            </div>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                </div>
                            ) : (
                                // Standard Approve/Reject UI (non-dynamic)
                                <div className="task-detail-actions">
                                    <Button 
                                        variant="danger" 
                                        className="task-detail-action-button" 
                                        onClick={handleReject}
                                    >
                                        Reject
                                    </Button>
                                    <Form.Control
                                        type="text"
                                        placeholder="My Comment about the Request"
                                        value={comment}
                                        onChange={e => setComment(e.target.value)}
                                        className="task-detail-comment-input"
                                    />
                                    <Button 
                                        variant="success" 
                                        className="task-detail-action-button" 
                                        onClick={handleApprove}
                                    >
                                        Approve
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : task.currentUserAction && task.currentUserAction !== 'Pending' ? (
                        <div className="task-detail-user-action">
                            <div className="alert alert-info">
                                Your action: {task.currentUserAction} at {formatDate(task.currentUserActionDate)}
                            </div>
                            {canEdit ? (
                                <Button 
                                    variant="warning" 
                                    className="task-detail-edit-button"
                                    onClick={handleEdit}
                                >
                                    Rollback Last Action
                                </Button>
                            ) : (
                                <div className="alert alert-warning">
                                    {task.status === 'Completed' || task.status === 'Rejected' || task.status === 'Cancelled' ? (
                                        `Cannot rollback action because task is ${task.status.toLowerCase()}.`
                                    ) : (
                                        "Cannot rollback action because other users have performed actions after yours."
                                    )}
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>
            </div>
        </Container>
    );
};

export default TaskDetailPage; 